import { createTheme } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";

import type { ShapeScale, SiteThemeConfig, SurfaceDensity } from "@/types/database";
import { fontCssStack } from "./font-catalog";
import { deriveThemeTokens } from "./derive-tokens";

export { FONT_CATALOG, SITE_FONT_OPTIONS, fontCssStack, useGoogleFonts } from "./font-catalog";
export { deriveThemeTokens } from "./derive-tokens";
export * from "./color-utils";

// A published unit site renders in the unit's OWN configured brand, never
// the Scouting Ops platform theme — @scoutingops/ui's createTheme only
// accepts a fixed 5-value brand-color enum, which can't represent an
// arbitrary hex a unit picks, so this builds a plain MUI theme instead of
// going through the shared package for this one surface. Route-level
// isolation (never nesting inside the platform's own ThemeProvider) lives
// in src/styles/theme/theme-provider.tsx.

export function isValidHexColor(value: string | null): value is string {
  return typeof value === "string" && /^#[0-9a-fA-F]{6}$/.test(value);
}

const SHAPE_RADIUS: Record<ShapeScale, number> = {
  square: 0,
  soft: 4,
  rounded: 8,
  pill: 999,
};

const FORM_PADDING_PX: Record<SiteThemeConfig["forms"]["padding"], number> = {
  sm: 8,
  md: 12,
  lg: 16,
};

// MUI's own spacing unit is 8px by default — density scales that factor
// directly, so every sx={{ p: N }}/spacing={N}} in the app (including this
// theme's own component overrides) shifts together instead of needing
// per-component density logic.
const DENSITY_SPACING_PX: Record<SurfaceDensity, number> = {
  compact: 6,
  comfortable: 8,
  spacious: 11,
};

// The actual font family key backing a button-font setting — 'inherit-*'
// isn't itself a loadable family, it just points at whichever one is.
// Exported so callers that load web fonts (useGoogleFonts) or just need
// the CSS stack can resolve the setting without duplicating this switch.
export function resolveButtonFontKey(config: SiteThemeConfig["fonts"]): SiteThemeConfig["fonts"]["headingFont"] {
  if (config.buttonFont === "inherit-heading") return config.headingFont;
  if (config.buttonFont === "inherit-body") return config.paragraphFont;
  return config.buttonFont;
}

function resolveButtonFontFamily(config: SiteThemeConfig["fonts"]): string {
  return fontCssStack(resolveButtonFontKey(config));
}

export function createUnitSiteTheme(config: SiteThemeConfig): Theme {
  const { palette, fonts, buttons, forms, surfaces } = config;
  const tokens = deriveThemeTokens(palette);
  // "Base Size" scales the whole type ramp proportionally (MUI's own
  // fontSize-coefficient mechanism), then pins the actual paragraph text
  // to the literal px value promised on the Fonts screen — the two
  // together keep headings scaling sensibly alongside body text rather
  // than only affecting one and not the other.
  const sizeRatio = fonts.baseSize / 16;

  const headingFontFamily = fontCssStack(fonts.headingFont);
  const paragraphFontFamily = fontCssStack(fonts.paragraphFont);
  const buttonFontFamily = resolveButtonFontFamily(fonts);
  const miscFontFamily = fontCssStack(fonts.miscFont);
  const buttonRadius = SHAPE_RADIUS[buttons.shape];

  return createTheme({
    palette: {
      primary: { main: palette.accent },
      secondary: { main: palette.dark },
      background: { default: palette.lightest, paper: palette.lightest },
      text: { primary: tokens.textOnLightest, secondary: palette.dark },
      divider: tokens.borderSubtle,
      success: { main: tokens.success },
      warning: { main: tokens.warning },
      error: { main: tokens.error },
    },
    shape: { borderRadius: buttonRadius },
    spacing: DENSITY_SPACING_PX[surfaces.density],
    typography: {
      fontFamily: paragraphFontFamily,
      fontSize: 14 * sizeRatio,
      h1: { fontFamily: headingFontFamily },
      h2: { fontFamily: headingFontFamily },
      h3: { fontFamily: headingFontFamily },
      h4: { fontFamily: headingFontFamily },
      h5: { fontFamily: headingFontFamily },
      h6: { fontFamily: headingFontFamily },
      subtitle1: { fontFamily: headingFontFamily },
      subtitle2: { fontFamily: headingFontFamily },
      body1: { fontFamily: paragraphFontFamily, fontSize: `${fonts.baseSize}px` },
      body2: { fontFamily: paragraphFontFamily, fontSize: `${Math.round(fonts.baseSize * 0.875)}px` },
      button: { fontFamily: buttonFontFamily, textTransform: "none" },
      caption: { fontFamily: miscFontFamily },
      overline: { fontFamily: miscFontFamily },
    },
    components: {
      // This theme's own CssBaseline (see PublicSite.tsx/StudioCanvas.tsx)
      // needs its own focus-ring color — global.css's *:focus-visible rule
      // references the platform theme's CSS variable, which doesn't exist
      // on a route that never nests inside the platform ThemeProvider (see
      // theme-provider.tsx's isPublicSiteRoute()).
      MuiCssBaseline: {
        styleOverrides: {
          "*:focus-visible": { outline: `2px solid ${tokens.focusRing}` },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { boxShadow: "none", borderRadius: buttonRadius },
          contained: {
            "&:hover": { backgroundColor: tokens.accentHover },
            "&:active": { backgroundColor: tokens.accentActive },
          },
          outlined: {
            "&:hover": { borderColor: tokens.accentHover, backgroundColor: `${palette.accent}14` },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: SHAPE_RADIUS[forms.fieldShape],
            backgroundColor: forms.fieldFill ? palette.light : "transparent",
          },
          notchedOutline: {
            border: forms.border === "outline" ? `1px solid ${tokens.borderStrong}` : "none",
          },
          input: {
            paddingTop: FORM_PADDING_PX[forms.padding],
            paddingBottom: FORM_PADDING_PX[forms.padding],
          },
        },
      },
    },
  });
}
